import React from 'react';
import { Alert, Button, Flex } from '@itandi/itandi-bb-ui';
import { toast } from 'react-toastify';
import { useRouter } from 'next/router';
import { PATH } from '~/pages/deals/[id]';
import { PATH as VENDOR_PATH } from '~/pages/vendors/deals/[id]';
import type { Deal } from '~/models/Deal';
import Styles from './ConstructionFooter.module.scss';
import { isOwnStatus } from '~/models/Status';
import { isSuccess } from '~/utils/api/api_handler';
import type { NskResponse } from '~/models/Api';
import { useOpenHandler } from '~/hooks/useOpenHandler';
import { ConfirmModal } from '../parts/modal/ConfirmModal';
import type { DealEstimationHistoryContext } from '~/models/DealEstimationHistory';
import type { DealEstimationHeader } from '~/models/DealEstimationHeader';
import { estimationRequestSide } from '~/models/DealEstimationHeader';
import { MEDIA_QUERY, useMediaQuery } from '~/hooks/useMediaQuery';

type Props = {
  deal: Deal;
  isAdmin: boolean;
  companyType: 'client' | 'vendor';
  dealEstimationHistoryContext: DealEstimationHistoryContext;
  requestHeader: DealEstimationHeader | null;
  handleUpdateRequestStatus: (
    dealId: number,
    dealEstimationHeaderId: number
  ) => Promise<NskResponse<void>>;
  handleUpdateCancelStatus: (
    dealId: number,
    dealEstimationHeaderId: number,
    status: 'cancel'
  ) => Promise<NskResponse<void>>;
  handleMutate: () => Promise<void>;
  handleMutateRequestHeader: () => Promise<void>;
};

export const ConstructionRequestFooter: React.FC<Props> = ({
  deal,
  isAdmin,
  companyType,
  dealEstimationHistoryContext,
  requestHeader,
  handleUpdateRequestStatus,
  handleUpdateCancelStatus,
  handleMutate,
  handleMutateRequestHeader
}: Props) => {
  const { match: isSp, isLoaded: windowLoaded } = useMediaQuery(MEDIA_QUERY.sp);
  const router = useRouter();
  const { dealConstructions } = dealEstimationHistoryContext;
  const [requestHeaderState, setRequestHeaderState] =
    React.useState<DealEstimationHeader | null>(requestHeader);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const {
    isOpen: isOpenRequest,
    handleOpen: handleOpenRequest,
    handleClose: handleCloseRequest
  } = useOpenHandler();
  const {
    isOpen: isOpenCancel,
    handleOpen: handleOpenCancel,
    handleClose: handleCloseCancel
  } = useOpenHandler();
  if (
    // 非共有案件で申請等の操作は行わない
    isOwnStatus(deal.status) ||
    !estimationRequestSide(deal, companyType)
  ) {
    return null;
  }
  const onClickRequest = async (): Promise<void> => {
    setIsSubmitting(true);
    if (dealEstimationHistoryContext.dealEstimationHeaderId === null) return;
    const result = await handleUpdateRequestStatus(
      deal.id,
      dealEstimationHistoryContext.dealEstimationHeaderId
    );
    if (isSuccess(result)) {
      await router.push(
        companyType === 'client' ? PATH(deal.id) : VENDOR_PATH(deal.id)
      );
      handleMutate();
      handleMutateRequestHeader();
      setIsSubmitting(false);
      toast.success('見積りを申請しました');
      return;
    }
    handleCloseRequest();
    setIsSubmitting(false);
    toast.error(result.message ?? 'エラーが発生しました。');
  };

  const onClickCancel = async (): Promise<void> => {
    if (requestHeader === null) return;
    setIsSubmitting(true);
    const result = await handleUpdateCancelStatus(
      deal.id,
      requestHeader.id,
      'cancel'
    );
    if (isSuccess(result)) {
      handleMutate();
      handleMutateRequestHeader();
      toast.success('申請中の見積りを取り下げました');
      handleCloseCancel();
      setRequestHeaderState(null);
      setIsSubmitting(false);
      return;
    }
    handleCloseCancel();
    setIsSubmitting(false);
    toast.error(result.message ?? 'エラーが発生しました。');
  };
  if (!windowLoaded) return null;
  return (
    <div className={Styles.ButtonArea}>
      <Flex
        gap="8px"
        justifyContent="right"
        marginTop="16px"
        padding="16px 8px"
      >
        <Button
          disabled={isAdmin || isSubmitting || requestHeaderState === null}
          onClick={handleOpenCancel}
          variant="primary"
          width={isSp ? 'fill' : 'default'}
        >
          見積りを編集する
        </Button>
        <Button
          disabled={isAdmin || isSubmitting || dealConstructions.length === 0}
          onClick={handleOpenRequest}
          variant="primary"
          width={isSp ? 'fill' : 'default'}
        >
          見積りを申請する
        </Button>
      </Flex>
      <ConfirmModal
        confirmType="save"
        handleAccept={onClickCancel}
        handleCancel={handleCloseCancel}
        isOpen={isOpenCancel}
      >
        申請中の見積りを編集しますか？
        <Alert marginTop=".5rem" variant="warning">
          現在申請されている申請内容は取り下げられます。
        </Alert>
      </ConfirmModal>
      <ConfirmModal
        confirmType="save"
        handleAccept={onClickRequest}
        handleCancel={handleCloseRequest}
        isOpen={isOpenRequest}
      >
        現在の見積り内容を申請してもよろしいですか？
        <Alert marginTop=".5rem" variant="warning">
          申請が確認先に反映されるまで、未確定の状態となります。
        </Alert>
      </ConfirmModal>
    </div>
  );
};
